// ============================================================================
// LA MENTION LÉGALE D'UN SMS MARKETING — ce qu'on ajoute au texte, et ce que ça coûte.
//
// POURQUOI CE MODULE EXISTE. Tout SMS de prospection doit porter un moyen de s'y opposer. Laisser
// l'exploitant y penser garantit qu'il manquera un jour, sur l'envoi qu'on n'a pas relu — et il part
// en masse. La mention est donc AJOUTÉE par le serveur, sur le seul chemin d'envoi.
//
// ⚠️ 🔴 ET ELLE COMPTE DANS LES CARACTÈRES FACTURÉS. C'est là que se joue l'argent : une mention
// d'une vingtaine de caractères ajoutée APRÈS le compteur fait basculer un message de 155 caractères
// d'UN segment à DEUX. Sur 20 000 destinataires, ce sont 20 000 SMS facturés que l'écran n'avait pas
// annoncés. L'assistant compte donc sur le corps RÉEL, mention comprise — jamais sur le texte saisi.
//
// ⚠️ MODULE PARTAGÉ PAR LES DEUX MOITIÉS DU BACK-OFFICE, et c'est la raison de sa forme : il n'importe
// RIEN, pour que le serveur (Node) et l'assistant (navigateur) l'emploient tel quel. Écrire le compte
// d'un côté et la composition de l'autre les ferait diverger à la première correction — et l'écran
// annoncerait alors un prix que l'envoi ne respecte pas.
// ============================================================================

/**
 * La mention par défaut — le numéro court de l'opérateur de la plateforme.
 * ⚠️ Un magasin qui change d'opérateur change de numéro : la mention se PASSE en paramètre partout
 * où elle sert, pour que le jour où elle vient d'un réglage, aucun appelant ne soit à réécrire.
 */
export const SMS_STOP_MENTION = 'STOP 36608';

/**
 * Le corps réellement envoyé : le texte saisi, puis la mention — jamais deux fois.
 *
 * ⚠️ L'EXPLOITANT CONSCIENCIEUX EST LE CAS À TRAITER. Celui qui a déjà tapé sa mention recevrait
 * sinon un message qui la porte deux fois : la seconde est payée, et elle donne l'air d'un envoi mal
 * fabriqué. On reconnaît une mention déjà présente au mot STOP ET au numéro, pas à la phrase exacte :
 * « STOP au 36608 » et « stop 36608 » sont la même mention pour un lecteur, et doivent l'être ici.
 *
 * ⚠️ UN TEXTE VIDE RESTE VIDE. Rendre la mention seule fabriquerait un SMS qui ne dit rien et que
 * personne n'a voulu ; c'est à la validation du corps de refuser une campagne sans message.
 */
export function withStopMention(text: string, mention: string = SMS_STOP_MENTION): string {
  const body = text.trim();
  if (!body) return '';
  return alreadyMentioned(body, mention) ? body : `${body}\n${mention}`;
}

/**
 * Vrai quand le texte porte déjà cette mention — le mot STOP et le numéro, dans n'importe quel ordre.
 * ⚠️ LE MOT SE CHERCHE SANS ÉGARD À LA CASSE (« stop 36608 » est une mention), le NUMÉRO n'en a pas :
 * mettre le texte en minuscules avant d'y chercher des chiffres ne protégeait de rien.
 */
function alreadyMentioned(text: string, mention: string): boolean {
  if (!/\bstop\b/i.test(text)) return false;
  const digits = mention.match(/\d+/g) ?? [];
  return digits.length > 0 && digits.every((d) => text.includes(d));
}

/**
 * Le nombre de segments facturés pour ce corps — ESTIMATION, et l'écran le dit.
 *
 * 160 caractères pour un seul segment, 153 dès qu'il y en a plusieurs (l'en-tête de concaténation en
 * mange 7). ⚠️ UN CARACTÈRE HORS TABLE GSM-7 BASCULE TOUT LE MESSAGE EN 70/67, ce qu'on ne peut pas
 * deviner sans la table du fournisseur — d'où « environ » à l'écran, plutôt qu'un chiffre affirmé.
 */
export function smsSegments(text: string): number {
  return text.length === 0 ? 0 : text.length <= 160 ? 1 : Math.ceil(text.length / 153);
}
